import PeopleIcon from '@mui/icons-material/People'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useGetRole } from 'api/auth/hooks'
import { useGetAllPublishers } from 'api/publisher/hooks'
import { Publisher } from 'api/publisher/types'
import Select from 'components/shared/inputs/Select'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { usePublisherStore } from 'store/publisher'
import { colors } from 'styles/theme'

function matchesSearch(search: string, ...texts: string[]) {
  return texts.some(
    (text) => text.toLowerCase().indexOf(search.toLowerCase()) > -1
  )
}

const PublisherDropdown = () => {
  const [searchText, setSearchText] = useState('')

  const { pathname } = useRouter()

  const { data: publishers } = useGetAllPublishers()

  const { userRole } = useGetRole()

  const setPublisherId = usePublisherStore((state) => state.setPublisherId)
  const publisherId = usePublisherStore((state) => state.publisherId)

  const currentPublisher = publishers?.find(
    (publisher: Publisher) => publisher.id === publisherId
  )

  const onPublisherChange = (e) => {
    setPublisherId(e.target.value)
    setSearchText('')
  }

  const displayedOptions = useMemo(
    () =>
      publishers
        ?.filter(
          (option: Publisher) =>
            matchesSearch(searchText, option.name, option.id) &&
            option.id !== publisherId
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [publishers, searchText, publisherId]
  )

  useEffect(() => {
    if (!publishers?.length) return

    const publisher = publishers.find(
      (publisher: Publisher) => publisher.id === publisherId
    )

    if (!publisher) {
      const firstPublisher = publishers[0]
      setPublisherId(firstPublisher.id)
    }
  }, [publishers, publisherId])

  if (!publishers?.length) return null

  if (
    pathname !== '/applications' &&
    pathname !== '/content-filters' &&
    pathname !== '/billing' &&
    pathname !== '/account'
  )
    return null

  if (userRole === 'publisher' && currentPublisher)
    return (
      <Box display="flex" flexDirection="column">
        <Typography id="publisher-name">
          Account Name: {currentPublisher.name}
        </Typography>
        <Typography id="publisher-id">
          Publisher ID: {currentPublisher.id}
        </Typography>
      </Box>
    )

  return (
    <Select
      id="publisherId"
      variant="standard"
      placeholder="Select publisher"
      value={publisherId}
      onChange={onPublisherChange}
      onClose={() => setSearchText('')}
      sx={selectSx}
      // This prevents rendering empty string in Select's value
      // if search text would exclude currently selected option.
      renderValue={() => (
        <Box display="flex" alignItems="center" gap="10px">
          <PeopleIcon />
          {
            publishers?.find(
              (publisher: Publisher) => publisher.id === publisherId
            )?.name
          }
        </Box>
      )}
    >
      <ListSubheader>
        <TextField
          size="small"
          placeholder="Search by name or ID..."
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              // Prevents autoselecting item while typing (default Select behaviour)
              e.stopPropagation()
            }
          }}
        />
      </ListSubheader>
      {currentPublisher && (
        <MenuItem sx={menuItemSx} value={publisherId}>
          <Box display="flex" alignItems="center" gap="10px">
            <PeopleIcon />
            <Typography sx={pubOverflow}>
              [{publisherId}] {currentPublisher.name}
            </Typography>
          </Box>
        </MenuItem>
      )}
      {displayedOptions?.map((publisher) => (
        <MenuItem sx={menuItemSx} key={publisher.id} value={publisher.id}>
          <Box display="flex" alignItems="center" gap="10px">
            <PeopleIcon />
            <Typography sx={pubOverflow}>
              [{publisher.id}] {publisher.name}
            </Typography>
          </Box>
        </MenuItem>
      ))}
    </Select>
  )
}

export default PublisherDropdown

const selectSx = {
  width: 260,
  ml: 3,
  backgroundColor: colors.gray[100],
  border: 'none',
  '& .MuiInputBase-input': {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
}

const menuItemSx = {
  maxWidth: 260,
}

const pubOverflow = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '200px',
}
